import { useMemo } from "react";
import { useLocationsMetadata, useProductsMetadata } from "./useMetadata";
import { ProductClass } from "../types/enums";
import type { MetadataFetchState, ProductMetadataBundle } from "../types/metadataState";
import type { ProductMetadataByLevel, LocationMetadataPayload } from "../types/metadata";

/**
 * Fetches the common set of metadata needed by most visualization charts:
 * location + all four product classification metadata sets.
 *
 * Accepts an optional `servicesClass` parameter for product classification
 * queries. App-level wrappers inject this from URL params.
 */
export const useVisualizationMetadata = (options?: { servicesClass?: string }) => {
  const locationsResult = useLocationsMetadata();
  const hs92Result = useProductsMetadata(ProductClass.HS92Products, options);
  const hs12Result = useProductsMetadata(ProductClass.HS12Products, options);
  const hs22Result = useProductsMetadata(ProductClass.HS22Products, options);
  const sitcResult = useProductsMetadata(ProductClass.SITCProducts, options);

  const locationMetadata: MetadataFetchState<LocationMetadataPayload> = useMemo(
    () => locationsResult as MetadataFetchState<LocationMetadataPayload>,
    [locationsResult],
  );

  const productMetadata: ProductMetadataBundle = useMemo(
    () => ({
      hs92: hs92Result as MetadataFetchState<ProductMetadataByLevel>,
      hs12: hs12Result as MetadataFetchState<ProductMetadataByLevel>,
      hs22: hs22Result as MetadataFetchState<ProductMetadataByLevel>,
      sitc: sitcResult as MetadataFetchState<ProductMetadataByLevel>,
    }),
    [hs92Result, hs12Result, hs22Result, sitcResult],
  );

  return {
    locationMetadata,
    productMetadata,
  };
};
